import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import FilterPod from './../../../../reusable/Filters/FilterPod.jsx';
import { debounce, merge, mergeWith, isArray, isEqual, forIn, intersection } from 'lodash';
import PESTLEFilter from './FilterPod/PESTLE.filter';
import RiskLevelFilter from './FilterPod/RiskLevel.filter';
import PeriodFilter from './FilterPod/Period.filters';
import NarrowByFilter from './FilterPod/NarrowBy.filter';
import EntitiesFilter from './FilterPod/Entities.filter';
import LanguageFilter from './FilterPod/Language.filter';
import PestleHelp from './../../../../reusable/PestleHelp/PestleHelp';
import utilities from '../../../../utils/utilities.js'
import api from '../../../../api/Common.api';
import commonActions from './../../../../api/Common.actions';
import categoriesApi from './../../../../api/Categories.api';
import xhr from './../../../../utils/xhr';
import { ENTITIES_FILTER_CATEGORIES, ENTITIES_FILTER_SPECIFIC, ENTITIES_FILTER_ALL_ENTITIES, FILTERS_DEBOUNCE_TIME, LANGUAGE_SELECT_OPTIONS } from "../../../../constants";

const TIMEOUT_REPAINT = 450;

export class DashboardFilters extends Component {
    static propTypes = {
        changeHandler: PropTypes.func.isRequired,
        filtersState: PropTypes.shape(FilterStateShape).isRequired,
        filtersCollapsed: PropTypes.object,
        onToggleVisibility: PropTypes.func
    };

    static defaultProps = {
        filtersCollapsed: {
            filtersEntireSection: false,
            filtersNarrowBy: false,
            filtersEntities: false,
            filtersRiskAngles: false,
            filtersRiskLevels: false,
            filtersDateRange: false,
            filtersLanguage: false
        },
        onToggleVisibility: () => {}
    }

    constructor(props) {
        super(props);

        this.state = {
            visible: !this.props.filtersCollapsed.filtersEntireSection,
            isFiltersOverflowVisible: false,
            categories: null,
            entities: null,
            filters: {
                entities: {
                    type: ENTITIES_FILTER_ALL_ENTITIES,
                    entitiesId: [],
                    categories: {
                        allCategories: true,
                        allUnassigned: true,
                        categoryIds: []
                    }
                },
                days: props.filtersState.days,
                category: Object.assign([], props.filtersState.category),
                pestle: Object.assign({}, props.filtersState.pestle),
                risk: Object.assign({}, props.filtersState.risk),
                language: Object.assign({}, props.filtersState.language) // TODO: To change to persisted
            },
            debounceLock: false
        };
        this.userFilters = {};

        this.toggleVisibility = this.toggleVisibility.bind(this);
        this.resetAllHandler = this.resetAllHandler.bind(this);
        this.toggleFiltersOverflow = this.toggleFiltersOverflow.bind(this);
    }

    componentWillMount() {
        this.fetchCategories();
        this.fetchEntities();
    }

    componentWillReceiveProps(nextProps) {
        if (!this.state.debounceLock) {  // lock prevents any incoming new props while debouncing
            this.setState({
                filters: {
                    entities: {
                        type: nextProps.filtersState.entities.type,
                        categories: Object.assign({}, nextProps.filtersState.entities.categories),
                        entitiesId: nextProps.filtersState.entities.entitiesId
                    },
                    days: nextProps.filtersState.days,
                    category: Object.assign([], nextProps.filtersState.category),
                    entity: Object.assign([], nextProps.filtersState.entity),
                    pestle: Object.assign({}, nextProps.filtersState.pestle),
                    risk: Object.assign({}, nextProps.filtersState.risk),
                    language: Object.assign({}, nextProps.filtersState.language)
                }
            }, () => {
                this.checkCategoryStillExists(nextProps, this.state.categories);
            })
        }
    }

    fetchCategories() {
        categoriesApi
            .getCategoryNames()
            .then(categories => {
                this.setState({ categories })
                this.checkCategoryStillExists(this.props, categories);
            })
            .catch((error) => {
                utilities.logError(error, 'Dashboard.filters.loadCategories(): ');
                xhr.handleAllErrors(error);
            });
    }

    checkCategoryStillExists(props, categories) {
        if (props.filtersState.entities.type === ENTITIES_FILTER_CATEGORIES && categories) {
            if (!props.filtersState.entities.categories.allCategories && !props.filtersState.entities.categories.allCategories
                && props.filtersState.entities.categories.categoriesId && props.filtersState.entities.categories.categoriesId.length) {
                let exists = false;
                props.filtersState.entities.categories.categoriesId.forEach(catId => {
                    if (categories[catId]) {
                        exists = true;
                    }
                })
                if (!exists) {
                    this.resetHandlers.entities();
                }
            }
        }
    }

    checkEntitiesStillExists(props, entities) {
        if (entities) {
            const entitiesIds = Object.keys(entities);
            if (props.filtersState.entities
                && props.filtersState.entities.type === ENTITIES_FILTER_SPECIFIC
                && props.filtersState.entities.entitiesId
                && props.filtersState.entities.entitiesId.length
                && intersection(entitiesIds, props.filtersState.entities.entitiesId).length === 0) {
                this.resetHandlers.entities();
            }
        }
    }

    fetchEntities() {
        api.getEntities()
            .then(entities => {
                this.setState({ entities })
                this.checkEntitiesStillExists(this.props, entities);
            })
            .catch((error) => {
                utilities.logError(error, 'Dashboard.filters.loadEntities(): ');
                xhr.handleAllErrors(error);
            });
    }

    updateFilters(filters, userFilters = null) {
        this.setState({
            // for arrays we just replace, no merging
            filters: mergeWith({}, this.state.filters, filters,
                (objValue, srcValue) => isArray(srcValue) ? srcValue : undefined)
        });
        // and same here for the prefs (which is essentially the same just the keys differ)
        this.userFilters = mergeWith({}, this.userFilters, userFilters,
            (objValue, srcValue) => isArray(srcValue) ? srcValue : undefined);

        this.triggerUpdateFilters();
        this.setState({ debounceLock: true });
    }

    triggerUpdateFilters = debounce(() => {
        this.setState({ debounceLock: false }, () => {
            if (!isEqual(this.state.filters, this.props.filtersState)) {
                this.props.changeHandler(this.state.filters, this.userFilters);
            }
        })
    }, FILTERS_DEBOUNCE_TIME, { trailing: true });

    toggleVisibility() {
        this.setState({ visible: !this.state.visible }, () => {
            this.props.onToggleVisibility('filtersEntireSection', !this.state.visible);
        });

        (this.timeout) && clearTimeout(this.timeout);
        this.timeout = setTimeout(() => { utilities.triggerWindowResize() }, TIMEOUT_REPAINT);
    }

    toggleFilterCollapsed = (filterKey) => () => {
        this.props.onToggleVisibility(filterKey, !this.props.filtersCollapsed[filterKey]);
    }

    resetAllHandler() {
        for (let i in this.resetHandlers) {
            setTimeout(() => { this.resetHandlers[i](); }, 0); // Set timeout to make the changes "async", to break them from the chain
        }
    }

    preProcessFilter(filter, defaultFilter) {
        let isEmpty = true;
        forIn(filter, (value) => {value && (isEmpty = false)});
        // If empty, use defaults
        if (isEmpty) {
            return defaultFilter
        } else {
            return filter
        }
    }

    toggleFiltersOverflow(isFiltersOverflowVisible) {
        this.setState({ isFiltersOverflowVisible: !isFiltersOverflowVisible });
    }

    changeHandlers = {
        entities:   (data)=> {
            let newEntities = Object.assign({}, { ...this.state.filters.entities, ...data })
            this.updateFilters(
                { entities: newEntities },
                { entitiesFilter: newEntities }
            );
        },
        entity:     (entities)  => { this.updateFilters({ entities }, { entitiesIdFilters:   entities});  },
        pestle:     (pestle)    => {
            pestle = this.preProcessFilter(pestle, defaults.pestle);
            this.updateFilters({ pestle }, { pestleFilter: pestle });
        },
        risk:       (risk)      => {
            risk = this.preProcessFilter(risk, defaults.risk);
            this.updateFilters({ risk }, { riskFilter: risk });
        },
        days:       (days)      => { this.updateFilters({ days }, {daysFilter: days}) },
        language:   (language)  => {
            language = this.preProcessFilter(language, defaults.language);
            this.updateFilters({ language }, { languageFilter: language });
        }
    };

    resetHandlers = {
        entities:   () => { this.changeHandlers.entities(Object.assign({}, defaults.entities)); },
        pestle:     () => { this.changeHandlers.pestle(defaults.pestle); },
        risk:       () => { this.changeHandlers.risk(defaults.risk); },
        days:       () => { this.changeHandlers.days(defaults.days); },
        language:   () => { this.changeHandlers.language(defaults.language); },
    };

    renderNarrowBy() {
        return <FilterPod title={<FormattedMessage id={"Dashboard_Charts_UIText_dashboard.filters.narrowBy"} />}
                          isOpen={!this.props.filtersCollapsed.filtersNarrowBy}
                          reset={<FormattedMessage id="Dashboard_CoreFunctionality_UIText_dashboard.resetAllFilters" />}
                          resetHandler={this.resetAllHandler}
                          onToggleVisibility={this.toggleFilterCollapsed('filtersNarrowBy')}>
            <NarrowByFilter changeHandlers={this.changeHandlers}
                            filtersState={this.state}
                            categories={this.state.categories}
                            entities={this.state.entities} />
        </FilterPod>;
    }

    renderEntitiesFilter() {
        return <FilterPod title={<FormattedMessage id={"RSS_Feed_UIText_feedListing.entities"} />}
                          isOpen={!this.props.filtersCollapsed.filtersEntities}
                          className='entities-filter'
                          reset={<FormattedMessage id="Dashboard_CoreFunctionality_UIText_dashboard.reset"/>}
                          resetHandler={this.resetHandlers.entities}
                          onToggleVisibility={this.toggleFilterCollapsed('filtersEntities')}>
            <EntitiesFilter changeHandlers={this.changeHandlers}
                            entitiesSelection={this.state.filters.entities}
                            categories={this.state.categories}
                            entities={this.state.entities}/>
        </FilterPod>;
    }

    renderPestleFilter() {
        return <FilterPod title={<FormattedMessage id={"Dashboard_Charts_UIText_dashboard.filters.riskAngles"} />}
                          isOpen={!this.props.filtersCollapsed.filtersRiskAngles}
                          helperIcon={<PestleHelp parentOverflowHandler={this.toggleFiltersOverflow} />}
                          reset={<FormattedMessage id="Dashboard_CoreFunctionality_UIText_dashboard.reset"/>}
                          resetHandler={this.resetHandlers.pestle}
                          onToggleVisibility={this.toggleFilterCollapsed('filtersRiskAngles')}>
            <PESTLEFilter switchPestle={this.changeHandlers.pestle} pestle={this.state.filters.pestle} />
        </FilterPod>;
    }

    renderRiskFilter() {
        return <FilterPod title={<FormattedMessage id={"Dashboard_Charts_UIText_dashboard.filters.riskLevels"} />}
                          isOpen={!this.props.filtersCollapsed.filtersRiskLevels}
                          reset={<FormattedMessage id="Dashboard_CoreFunctionality_UIText_dashboard.reset"/>}
                          resetHandler={this.resetHandlers.risk}
                          onToggleVisibility={this.toggleFilterCollapsed('filtersRiskLevels')}>
            <RiskLevelFilter switchRisk={this.changeHandlers.risk} risk={this.state.filters.risk}/>
        </FilterPod>;
    }

    renderPeriodFilter() {
        return <FilterPod title={<FormattedMessage id={"Dashboard_Charts_UIText_dashboard.filters.dateRange"} />}
                          isOpen={!this.props.filtersCollapsed.filtersDateRange}
                          reset={<FormattedMessage id="Dashboard_CoreFunctionality_UIText_dashboard.reset"/>}
                          resetHandler={this.resetHandlers.days}
                          onToggleVisibility={this.toggleFilterCollapsed('filtersDateRange')}>
            <PeriodFilter switchDays={this.changeHandlers.days} days={this.state.filters.days}/>
        </FilterPod>;
    }

    renderLanguageFilter() {
        return <FilterPod title={<FormattedMessage id={"Dashboard_Charts_UIText_dashboard.filters.languageFilter"} />}
                          isOpen={!this.props.filtersCollapsed.filtersLanguage}
                          reset={<FormattedMessage id="Dashboard_CoreFunctionality_UIText_dashboard.reset"/>}
                          resetHandler={this.resetHandlers.language}
                          onToggleVisibility={this.toggleFilterCollapsed('filtersLanguage')}>
            <LanguageFilter switchLanguage={this.changeHandlers.language} languages={this.state.filters.language}/>
        </FilterPod>;
    }

    renderFilters() {
        return <Fragment>
            {this.renderNarrowBy()}
            {this.renderEntitiesFilter()}
            {this.renderPestleFilter()}
            {this.renderRiskFilter()}
            {this.renderPeriodFilter()}
            {this.renderLanguageFilter()}
        </Fragment>
    }

    render() {
        return(
            <div className={"dashboard-page__filters " + (this.state.visible ? "" : "closed")}>
                <div className={"dashboard-page__filters__container" + (this.state.isFiltersOverflowVisible ? " overflow-visible" : " overflow-hidden")}>
                    <div className="toggle" onClick={this.toggleVisibility}>
                        <FormattedMessage id="Dashboard_CoreFunctionality_UIText_dashboard.filters"/>
                    </div>
                    {this.renderFilters()}
                </div>
            </div>
        )
    }
}

export default injectIntl(DashboardFilters);

// Move ---
export const PestleShape = {
    political:     PropTypes.bool,
    economic:      PropTypes.bool,
    social:        PropTypes.bool,
    technological: PropTypes.bool,
    legal:         PropTypes.bool,
    environmental: PropTypes.bool
};

export const RiskShape = {
    high: PropTypes.bool,
    medium: PropTypes.bool,
    low: PropTypes.bool
};

export const categoryFilterShape = {
    allCategories: PropTypes.bool,
    allUnassigned: PropTypes.bool,
    categoriesId: PropTypes.array
};

export const entitiesFilterShape = {
    type: PropTypes.oneOf([ENTITIES_FILTER_CATEGORIES, ENTITIES_FILTER_SPECIFIC, ENTITIES_FILTER_ALL_ENTITIES]),
    entitiesId: PropTypes.array,
    categories: PropTypes.shape(categoryFilterShape)
};

export const FilterStateShape = PropTypes.shape({
    entities:   PropTypes.shape(entitiesFilterShape),
    days:       PropTypes.number,
    category:   PropTypes.array,
    pestle:     PropTypes.shape(PestleShape),
    risk:       PropTypes.shape(RiskShape)
});

export const LanguageFilterShape = LANGUAGE_SELECT_OPTIONS;

export const defaults = {
    days:     30,
    category: [],
    entities: {
        type: ENTITIES_FILTER_ALL_ENTITIES,
        entitiesId: [],
        categories: {
            allCategories: true,
            allUnassigned: true,
            categoriesId: []
        }
    },
    pestle:   {
        political:     true,
        economic:      true,
        social:        true,
        technological: true,
        legal:         true,
        environmental: true
    },
    risk:     {
        high:   true,
        medium: false,
        low:    false
    },
    language: {
        en: true,
        de: true,
        fr: true
    }
};



// WEBPACK FOOTER //
// ./src/scripts/pages/StartPage/Dashboard/components/DashboardFilters.jsx